import React, { useCallback, useEffect, useState } from "react";
import {
	Button,
	Chip,
	CircularProgress,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiRefresh, mdiSkull, mdiCodeJson, mdiCheckCircleOutline } from "@mdi/js";
import {
	BackofficeAdminServices,
	NotistackServices,
	DeadLetterJobsFilters,
	DeadLetterJobsResponse,
	PublicDeadLetterJobDTO,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

/**
 * EK-280 — Dead Letter Queue (jobs BullMQ que esgotaram attempts).
 */

function fmtDate(iso: string | null): string {
	if (!iso) return "—";
	return new Date(iso).toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

const PAGE_SIZE = 50;

export const DeadLetterJobsPage: React.FC = () => {
	const [data, setData] = useState<DeadLetterJobsResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [expanded, setExpanded] = useState<string | null>(null);

	// Filtros
	const [queueName, setQueueName] = useState("");
	const [jobName, setJobName] = useState("");
	const [accountId, setAccountId] = useState("");
	const [statusFilter, setStatusFilter] = useState<"all" | "unresolved" | "resolved">("all");
	const [offset, setOffset] = useState(0);

	const fetchData = useCallback(async () => {
		setLoading(true);
		const filters: DeadLetterJobsFilters = { limit: PAGE_SIZE, offset };
		if (queueName.trim()) filters.queueName = queueName.trim();
		if (jobName.trim()) filters.jobName = jobName.trim();
		if (accountId.trim()) filters.accountId = accountId.trim();
		if (statusFilter === "unresolved") filters.unresolved = true;
		// statusFilter === "resolved" handled client-side (rare case)

		try {
			const response = await BackofficeAdminServices.listDeadLetterJobs(filters);
			setData(response);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar DLQ");
		} finally {
			setLoading(false);
		}
	}, [queueName, jobName, accountId, statusFilter, offset]);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	const handleApplyFilters = () => {
		setOffset(0);
		void fetchData();
	};

	const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>
						<Icon path={mdiSkull} size={1} /> Dead Letter Queue
					</h1>
					<p className={styles.subtitle}>
						EK-280 · Jobs BullMQ que esgotaram todas as tentativas (attempts). Payload completo disponível para reprocessing manual.
					</p>
				</div>
				<div className={styles.actions}>
					<Button
						size="small"
						variant="outlined"
						onClick={() => void fetchData()}
						disabled={loading}
						startIcon={<Icon path={mdiRefresh} size={0.8} />}
					>
						Recarregar
					</Button>
				</div>
			</div>

			<div className={styles.filters}>
				<TextField
					size="small"
					placeholder="Queue (ekeeps-dev-default, ...)"
					value={queueName}
					onChange={(e) => setQueueName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<TextField
					size="small"
					placeholder="Job name (task-cycle, ...)"
					value={jobName}
					onChange={(e) => setJobName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<TextField
					size="small"
					placeholder="Account ID"
					value={accountId}
					onChange={(e) => setAccountId(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
				/>
				<Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
					<MenuItem value="all">Todos</MenuItem>
					<MenuItem value="unresolved">Em aberto</MenuItem>
					<MenuItem value="resolved">Resolvidos</MenuItem>
				</Select>
				<Button variant="contained" size="small" onClick={handleApplyFilters}>
					Filtrar
				</Button>
			</div>

			{loading && !data ? (
				<div className={styles.loadingState}>
					<CircularProgress size={32} />
				</div>
			) : !data || data.items.length === 0 ? (
				<div className={styles.empty}>
					<p>🎉 Nenhum job em DLQ (com os filtros aplicados).</p>
				</div>
			) : (
				<>
					<div className={styles.summary}>
						<span>
							<strong>{data.total}</strong> total · página <strong>{currentPage}/{totalPages}</strong>
						</span>
						<div className={styles.pager}>
							<Button size="small" variant="outlined" onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0 || loading}>
								‹ Anterior
							</Button>
							<Button
								size="small"
								variant="outlined"
								onClick={() => setOffset((o) => o + PAGE_SIZE)}
								disabled={!data || offset + PAGE_SIZE >= data.total || loading}
							>
								Próxima ›
							</Button>
						</div>
					</div>

					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Quando</th>
									<th>Status</th>
									<th>Fila</th>
									<th>Job</th>
									<th>Attempts</th>
									<th>Account</th>
									<th>Reason</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{data.items
									.filter((row) => statusFilter !== "resolved" || row.resolvedAt !== null)
									.map((row) => (
										<RowItem
											key={row.id}
											row={row}
											expanded={expanded === row.id}
											onToggle={() => setExpanded((e) => (e === row.id ? null : row.id))}
										/>
									))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</div>
	);
};

const RowItem: React.FC<{ row: PublicDeadLetterJobDTO; expanded: boolean; onToggle: () => void }> = ({
	row,
	expanded,
	onToggle,
}) => {
	return (
		<>
			<tr className={styles.row} onClick={onToggle}>
				<td className={styles.colDate}>{fmtDate(row.finalFailedAt)}</td>
				<td>
					{row.resolvedAt ? (
						<Chip
							size="small"
							icon={<Icon path={mdiCheckCircleOutline} size={0.7} />}
							label="Resolvido"
							color="success"
							variant="outlined"
						/>
					) : (
						<Chip size="small" icon={<Icon path={mdiSkull} size={0.7} />} label="Em aberto" color="error" variant="filled" />
					)}
				</td>
				<td className={styles.mono}>{row.queueName}</td>
				<td className={styles.mono}>{row.jobName}</td>
				<td className={styles.mono}>
					{row.attemptsMade}/{row.attemptsMax}
				</td>
				<td className={styles.colAccount} title={row.accountId ?? ""}>
					{row.accountId ? `${row.accountId.slice(0, 8)}…` : "—"}
				</td>
				<td className={styles.reason}>
					<Tooltip title={row.failedReason} placement="top-start">
						<span>{row.failedReason.slice(0, 80)}{row.failedReason.length > 80 ? "…" : ""}</span>
					</Tooltip>
				</td>
				<td>
					<IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
						<Icon path={mdiCodeJson} size={0.7} />
					</IconButton>
				</td>
			</tr>
			{expanded && (
				<tr className={styles.expandedRow}>
					<td colSpan={8}>
						<div className={styles.expandedContent}>
							<div className={styles.errorBox}>
								<strong>Erro:</strong> {row.failedReason}
								{row.errorCode && <span className={styles.errorCode}> (code: {row.errorCode})</span>}
							</div>
							{row.errorStack && (
								<div className={styles.previewSection}>
									<h4>Stack trace</h4>
									<pre className={styles.json}>{row.errorStack}</pre>
								</div>
							)}
							<div className={styles.previewSection}>
								<h4>Job data (payload completo)</h4>
								<pre className={styles.json}>{JSON.stringify(row.jobData, null, 2)}</pre>
							</div>
							<div className={styles.timeline}>
								<span>Criado: {fmtDate(row.jobCreatedAt)}</span>
								<span>1ª tentativa: {fmtDate(row.firstAttemptedAt)}</span>
								<span>Falhou em: {fmtDate(row.finalFailedAt)}</span>
								{row.resolvedAt && <span>Resolvido em: {fmtDate(row.resolvedAt)}</span>}
							</div>
							<div className={styles.idsRow}>
								<span className={styles.idChip}>bullmq: {row.bullmqJobId}</span>
								{row.workspaceId && <span className={styles.idChip}>ws: {row.workspaceId.slice(0, 8)}…</span>}
								{row.teamId && <span className={styles.idChip}>team: {row.teamId.slice(0, 8)}…</span>}
								{row.taskId && <span className={styles.idChip}>task: {row.taskId.slice(0, 8)}…</span>}
							</div>
						</div>
					</td>
				</tr>
			)}
		</>
	);
};
